import React, { useState, useEffect, ReactNode } from "react";
import { Select, Input, Button, Alert, Spin, Tooltip } from "antd";
import axios from "axios";
import BarcodeScannerComponent from "react-qr-barcode-scanner";
import { Result } from "@zxing/library";
import { useHistory, useParams } from "react-router-dom";
import { IsOpenType } from "interfaces/events";
import { QuestionCircleOutlined } from "@ant-design/icons";

const { Option } = Select;

interface Event {
  id: number;
  name: string;
  start: string;
  is_open: number;
}

type AlertType = "info" | "success" | "warning" | "error";
type AlertMessage = string | ReactNode;

const TakeAttendanceForEvent: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<number | null>(null);
  const [username, setUsername] = useState("");
  const [isScanning, setIsScanning] = useState(false);
  const [loadingScan, setLoadingScan] = useState(false);
  const [viewFinderColor, setViewFinderColor] = useState("var(--primary-color)");
  const { id } = useParams<{ id: string }>();

  const history = useHistory();

  const [viewFinderPosition, setViewFinderPosition] = useState({
    left: "50%",
    top: "50%",
    width: "150px",
    height: "150px",
    transform: "translate(-50%, -50%)",
  });

  const [isAlertVisible, setIsAlertVisible] = useState(true);
  const [alertInfo, setAlertInfo] = useState<{
    type: AlertType;
    message: AlertMessage;
  }>({
    type: "info",
    message: "Begynn å ta oppmøte!",
  });

  useEffect(() => {
    if (id) {
      axios
        .get(`/events/admin-events/${id}/`)
        .then(response => {
          setEvents([response.data]);
          setSelectedEvent(response.data.id);
        })
        .catch(() => {
          // If the event is not found, redirect to the page where you can select all closedfuture events
          history.push("/admin/tools/take-attendance-for-event/");
        });
    } else {
      axios
        .get("/events/future-events/")
        .then(response => {
          const filteredEvents = response.data.filter((event: Event) => event.is_open === IsOpenType.CLOSED);
          setEvents(filteredEvents);
        })
        .catch(() => {
          setAlertInfo({
            type: "error",
            message: "Kunne ikke hente arrangementer. Prøv igjen.",
          });
        });
    }
  }, [id, history]);

  const showAlert = (alertType: AlertType, msg: AlertMessage, boxColor?: string) => {
    setAlertInfo({ type: alertType, message: msg });
    setIsAlertVisible(true);

    if (boxColor) {
      setViewFinderColor(boxColor);
      setTimeout(() => {
        setViewFinderColor("var(--primary-color)");
      }, 2000);
    }
  };

  const registerAttendance = async (inputUsername: string) => {
    if (!selectedEvent) {
      showAlert("error", <span>Du må velge arrangementet før du kan registrere oppmøte.</span>);
      return;
    }

    setLoadingScan(true);
    try {
      await axios.post(`/events/${selectedEvent}/register-attendance/`, {
        username: inputUsername,
      });

      showAlert(
        "success",
        <span>
          Oppmøte registrert for: <strong>{inputUsername}</strong>
        </span>,
        "green"
      );
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const errorMsg = error.response?.data?.error || "Kunne ikke registrere oppmøte, prøv igjen.";
        let alertType: "error" | "warning" = "error";
        let boxColor = "red";
        let finalMsg: ReactNode = (
          <span>
            Kunne ikke registrere oppmøte for <strong>{inputUsername}</strong>. Prøv igjen.
          </span>
        );

        if (errorMsg === "Username is required.") {
          finalMsg = <span>Brukernavn er påkrevd.</span>;
        } else if (errorMsg === "Student do not exist.") {
          finalMsg = <span>Studenten finnes ikke.</span>;
        } else if (errorMsg === "Event not found.") {
          finalMsg = <span>Arrangementet ble ikke funnet.</span>;
        } else if (errorMsg === "Student is not registered for this event.") {
          finalMsg = (
            <span>
              Studenten <strong>{inputUsername}</strong> er ikke registrert for dette arrangementet.
            </span>
          );
        } else if (errorMsg === "Attendance has already been recorded.") {
          finalMsg = (
            <span>
              Oppmøte er allerede registrert for: <strong>{inputUsername}</strong>
            </span>
          );
          alertType = "warning";
          boxColor = "orange";
        }

        showAlert(alertType, finalMsg, boxColor);
      } else {
        showAlert(
          "error",
          <span>
            Ukjent feil ved registrering av <strong>{inputUsername}</strong>. Prøv igjen.
          </span>,
          "red"
        );
      }
    } finally {
      setUsername("");
      setLoadingScan(false);
    }
  };

  const handleScan = (err: unknown, result?: Result) => {
    if (err) return;

    if (result) {
      const scannedUsername = result.getText();
      registerAttendance(scannedUsername);

      const points = result.getResultPoints();
      if (points && points.length > 0) {
        const minX = Math.min(...points.map(p => p.getX()));
        const maxX = Math.max(...points.map(p => p.getX()));
        const minY = Math.min(...points.map(p => p.getY()));
        const maxY = Math.max(...points.map(p => p.getY()));

        const width = maxX - minX;
        const height = maxY - minY;
        const size = Math.max(width, height) * 1.2;

        setViewFinderPosition({
          left: `${minX - (size - width) / 2}px`,
          top: `${minY - (size - height) / 2}px`,
          width: `${size}px`,
          height: `${size}px`,
          transform: "none",
        });
      }
    }
  };

  const handleStartScanning = () => {
    if (!selectedEvent) {
      showAlert("error", "Du må velge et arrangement før du kan registrere oppmøte.");
      return;
    }
    setIsScanning(true);
  };

  const handleStopScanning = () => {
    setIsScanning(false);
  };

  const handleManualAttendance = () => {
    if (!username) {
      showAlert("error", "Skriv inn brukernavn før du registrerer oppmøte.");
      return;
    }
    registerAttendance(username);
  };

  return (
    <div className="register-attendance-container" style={{ padding: "20px", textAlign: "center" }}>
      <h1>Registrer Oppmøte</h1>

      {/* Event Selection + Start/Stop */}
      <div style={{ maxWidth: "600px", margin: "0 auto 1em" }}>
        <Select
          placeholder="Velg et arrangement"
          style={{ width: "calc(100% - 24px)", marginBottom: "1em", display: "inline-block" }}
          onChange={value => setSelectedEvent(Number(value))}
          value={selectedEvent ?? undefined}
          disabled={!!id}
        >
          {events.map(event => (
            <Option key={event.id} value={event.id}>
              {event.name} - {new Date(event.start).toLocaleString()}
            </Option>
          ))}
        </Select>
        <Tooltip
          title={
            id
              ? "Siden du har valgt et bestemt arrangement, vises kun dette arrangementet i listen. Åpne innslippsappen via verktøy i sidemenyen for å velge et annet arrangement."
              : "I denne listen vises kun arrangementer som er stengt og som skjer i dag eller senere."
          }
        >
          <QuestionCircleOutlined style={{ marginLeft: "8px", fontSize: "16px", verticalAlign: "middle" }} />
        </Tooltip>

        {!isScanning ? (
          <Button onClick={handleStartScanning} block style={{ marginBottom: "1em" }} disabled={!selectedEvent}>
            Start Scanning
          </Button>
        ) : (
          <Button onClick={handleStopScanning} block style={{ marginBottom: "1em" }}>
            Stop Scanning
          </Button>
        )}
      </div>

      {/* Camera Feed / Scanner */}
      {isScanning && (
        <div style={{ margin: "1em auto" }}>
          <div style={{ position: "relative", width: "640px", height: "480px", margin: "0 auto" }}>
            <div
              style={{
                transform: "scaleX(-1)",
                width: "100%",
                height: "100%",
                position: "relative",
              }}
            >
              <BarcodeScannerComponent width={640} height={480} delay={2000} onUpdate={handleScan} />

              {/* Bounding Box */}
              <div
                style={{
                  position: "absolute",
                  border: `3px solid ${viewFinderColor}`,
                  pointerEvents: "none",
                  ...viewFinderPosition,
                }}
              />

              {/* Spinner while registering attendance */}
              {loadingScan && (
                <Spin
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 1,
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Alert */}
      {alertInfo && isAlertVisible && (
        <div style={{ maxWidth: "600px", margin: "1em auto" }}>
          <Alert
            type={alertInfo.type}
            message={alertInfo.message}
            showIcon
            closable
            onClose={() => setIsAlertVisible(false)}
          />
        </div>
      )}

      {/* Manual Username Entry */}
      <div style={{ maxWidth: "600px", margin: "1em auto" }}>
        <Input
          placeholder="Skriv inn brukernavn"
          aria-label="Skriv inn brukernavn"
          style={{ marginBottom: "1em" }}
          value={username}
          onChange={e => setUsername(e.target.value)}
          disabled={!selectedEvent}
        />
        <Button
          type="primary"
          onClick={handleManualAttendance}
          block
          aria-label="Registrer oppmøte"
          disabled={!selectedEvent}
        >
          Registrer Oppmøte
        </Button>
      </div>
    </div>
  );
};

export default TakeAttendanceForEvent;
